import React, { useCallback, useEffect, useMemo } from "react";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
import ScatterChart from "../../../../components/ScatterChart";
// hooks
import useScatterChart from "../../../../components/ScatterChart/useScatterChart";
// local components
import ComparisonDropdowns from "./ComparisonDropdowns";

// store
import useStore from "../../../../store";

import {
	useCustomFactorsIncludedInSatisfaction,
	useFactorComparison,
	usePageSettings,
	useUpdatePageSettings,
} from "../../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

// config
import Config from "../../../../Config";
// schema
import { IFactorComparisonPoint, ScatterAxis } from "../../../../schema";
// css
import css from "./FactorComparison.module.scss";

/**
 * Hook to handle custom factor options and labels
 */
const useCustomFactorOptions = () => {
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", undefined);
	const { data: customFactors } = useCustomFactorsIncludedInSatisfaction({ enabled: customFactorsEnabled });

	const customFactorNames = useMemo(
		() => customFactors?.customFactors?.map((factor) => factor.name) ?? [],
		[customFactors]
	);

	const allOptions = useMemo(
		() => [...Config.FACTORS.FACTORS, ...(customFactorsEnabled ? customFactorNames : []), "all", "eNPS"],
		[customFactorsEnabled, customFactorNames]
	);

	const labelsMap = useMemo(
		() =>
			allOptions.reduce<Record<ScatterAxis, string>>(
				(acc, curr) => {
					acc[curr] = curr === "all" ? "Satisfaction" : curr === "eNPS" ? "eNPS Rating" : curr;
					return acc;
				},
				{} as Record<ScatterAxis, string>
			),
		[allOptions]
	);

	return {
		allOptions,
		labelsMap,
	};
};

/**
 * Renders a factor comparison scatetr chart inside a card
 * with options to change the x and y axis
 */
const FactorComparison = () => {
	const [filter] = useStore((state) => [state.filter]);

	// derive axes from page settings
	const pageSettings = usePageSettings();
	const setPageSettings = useUpdatePageSettings().mutate;
	const { allOptions, labelsMap } = useCustomFactorOptions();

	// setAxis mutations
	const setXAxis = useCallback(
		(axis: ScatterAxis) => {
			setPageSettings({
				settings_path: "factors.scatter.xAxis",
				settings_value: axis,
			});
		},
		[setPageSettings]
	);
	const setYAxis = useCallback(
		(axis: ScatterAxis) => {
			setPageSettings({
				settings_path: "factors.scatter.yAxis",
				settings_value: axis,
			});
		},
		[setPageSettings]
	);

	const xAxis = pageSettings.data?.factors?.scatter?.xAxis || "Opportunity";
	const yAxis = pageSettings.data?.factors?.scatter?.yAxis || "all";

	// Check if selected axis is invalid given current options (e.g., custom factor selected but FF disabled)
	const isAxisInvalid = useCallback((axis: ScatterAxis) => !allOptions.includes(axis), [allOptions]);
	const needsAxisReset = isAxisInvalid(xAxis) || isAxisInvalid(yAxis);

	useEffect(() => {
		if (isAxisInvalid(xAxis)) setXAxis("Opportunity");
		if (isAxisInvalid(yAxis)) setYAxis("all");
	}, [isAxisInvalid, xAxis, yAxis, setXAxis, setYAxis]);

	// Skip the query when axes are about to be reset
	const { data, isLoading } = useFactorComparison(
		{
			xAxis,
			yAxis,
			startDate: filter.startDate ? filter.startDate : undefined,
			endDate: filter.endDate,
		},
		{ enabled: !needsAxisReset }
	);

	const validData = data || ({} as IFactorComparisonPoint[]);
	const { options, prepareDataForChart, getScaledOptions } = useScatterChart({
		xAxisLabel: labelsMap[xAxis],
		yAxisLabel: labelsMap[yAxis],
		data: validData,
	});

	/**
	 * returns the options for an eNPS axis to be used when either axis selects eNPS
	 */
	const getEnpsOptions = (axis: "x" | "y") => {
		return {
			...(data?.length
				? {}
				: // shows full scale when no enps data and enps selected
					{
						min: 0,
						max: 10,
					}),
			// and eNPS always gets increased step size
			ticks: {
				...(options.scales?.[axis]?.ticks || {}),
				stepSize: 2,
			},
		};
	};

	/**
	 * Scaled options adjusts the scaling and step size of the chart when the
	 * axis is eNPS
	 */
	const scaledOptions = getScaledOptions(
		xAxis === "eNPS"
			? getEnpsOptions("x")
			: // default to 1-5 scale
				{ min: 1, max: 5 },
		yAxis === "eNPS"
			? getEnpsOptions("y")
			: // default to 1-5 scale
				{ min: 1, max: 5 }
	);

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Factor Comparison
						</Text>
					</span>
				}
				info="This chart displays individual points for each anonymous employee comparing their satisfaction factors for the selected period."
				toolBar={
					<ComparisonDropdowns
						xAxis={xAxis}
						yAxis={yAxis}
						setXAxis={setXAxis}
						setYAxis={setYAxis}
						allOptions={allOptions}
					/>
				}
			/>
			<CardBody>
				<div className={css.cardBody}>
					{isLoading && (
						<div className={css.spinner}>
							<Spinner size={20} />
						</div>
					)}
					{!isLoading && data && data.length <= 0 && (
						<div className={css.emptyChartContainer}>
							<EmptyChart type="scatterLarge" />
						</div>
					)}
					{!isLoading && data && data.length > 0 && (
						<div className={css.scatterChart}>
							<ScatterChart data={prepareDataForChart()} options={scaledOptions} />
						</div>
					)}
				</div>
			</CardBody>
		</>
	);
};

export default FactorComparison;
